import Image from "next/image";
import { ArrowRight, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatFreePreviewCountLabel } from "@/lib/payments/free-preview-config";

type HowItWorksStep = {
  step: string;
  title: string;
  description: string;
  image: string;
  alt: string;
};

interface HowItWorksSectionProps {
  steps: readonly HowItWorksStep[];
  onTryItNow: () => void;
}

export function HowItWorksSection({
  steps,
  onTryItNow,
}: HowItWorksSectionProps) {
  return (
    <section
      id="how-it-works"
      className="relative overflow-hidden bg-[#0A0C0F] px-6 py-24 md:py-32"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-80 w-[60rem] -translate-x-1/2 rounded-full bg-white/[0.035] blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div className="grid items-end gap-6 md:grid-cols-[1fr_auto]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/35">
              Three simple steps
            </p>
            <h2 className="mt-4 max-w-2xl text-4xl font-bold tracking-tight text-white md:text-6xl">
              From your photo to a new wheel look
            </h2>
          </div>
          <p className="max-w-sm text-base leading-relaxed text-white/50 md:pb-2 md:text-right">
            Upload your car, choose the wheel, and compare the result before
            buying the real set.
          </p>
        </div>

        <div className="relative mt-12 rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.055] to-white/[0.02] p-3 shadow-2xl shadow-black/25 md:p-5">
          <div className="grid gap-3 md:grid-cols-3">
            {steps.map((item, index) => {
              const isWheelStep = index === 1;

              return (
                <article
                  key={item.step}
                  className="group relative rounded-[1.4rem] border border-white/[0.07] bg-[#0D1014] p-4 md:p-5"
                >
                  <div
                    className={cn(
                      "relative aspect-[4/3] overflow-hidden rounded-2xl",
                      isWheelStep ? "bg-[#F1F1EF]" : "bg-black"
                    )}
                  >
                    <Image
                      src={item.image}
                      alt={item.alt}
                      fill
                      className={cn(
                        "transition-transform duration-500",
                        isWheelStep
                          ? "object-contain p-7 group-hover:scale-[1.03] md:p-9"
                          : "object-cover group-hover:scale-[1.02]"
                      )}
                      sizes="(max-width: 768px) 100vw, 33vw"
                    />
                    <span className="absolute left-3 top-3 rounded-full border border-black/10 bg-white/90 px-3 py-1.5 text-xs font-semibold text-black shadow-sm">
                      {index === 0
                        ? "Your car"
                        : index === 1
                          ? "Your wheel"
                          : "Your preview"}
                    </span>
                  </div>

                  <div className="flex gap-4 px-1 pb-2 pt-6">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-sm font-semibold text-white/60">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-2xl font-semibold text-white">
                        {item.title}
                      </h3>
                      <p className="mt-2 leading-relaxed text-white/50">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  {index < steps.length - 1 ? (
                    <div className="absolute -right-5 top-[36%] z-20 hidden h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-[#171B21] text-white shadow-xl md:flex">
                      <ChevronRight className="h-4 w-4" aria-hidden />
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-5 rounded-2xl border border-white/[0.07] bg-white/[0.025] px-6 py-5 sm:flex-row">
          <p className="text-center text-sm text-white/45 sm:text-left">
            Start with {formatFreePreviewCountLabel()}. No credit card required.
          </p>
          <Button
            onClick={onTryItNow}
            size="lg"
            className="w-full bg-white text-black hover:bg-white/90 sm:w-auto"
          >
            Create my free preview
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}
